import { useTranslation } from 'react-i18next';

import type { LocationModel } from '@/entities/location';

import type { LocationWithData } from '../model/map-provider.interface';
import { extractLocationData, getDataLayerLabel, type DataLayer } from '../lib/location-popup.utils';

import { LocationPopupBreakdown } from './location-popup-breakdown';
import { LocationPopupTrend } from './location-popup-trend';

interface LocationPopupProps {
  location: LocationModel | LocationWithData;
  dataLayer?: DataLayer;
  currentWeek?: number;
}

export function LocationPopup({ location, dataLayer = 'stock', currentWeek = 0 }: LocationPopupProps) {
  const { t } = useTranslation();
  const { dataValue, hasData, dataProgress } = extractLocationData(location);

  return (
    <div className="p-3 min-w-[300px] max-w-[400px]">
      <div className="mb-3 border-b pb-2">
        <h3 className="font-semibold text-lg">{location.displayName}</h3>
        <p className="text-sm text-gray-600">
          {location.typeName} • {location.name}
        </p>
        <p className="text-xs text-gray-500">
          {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
        </p>
      </div>

      {hasData && (
        <div className="mb-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium text-gray-700">{getDataLayerLabel(dataLayer, t)}</span>
            <span className="text-lg font-bold text-blue-600">{dataValue.toLocaleString()} MT</span>
          </div>
          {currentWeek !== 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {t('map.popup.weekLabel', 'Week')} {currentWeek} ({Math.abs(currentWeek)}{' '}
              {t('map.popup.weeksAgo', 'weeks ago')})
            </p>
          )}
        </div>
      )}

      {dataLayer === 'stock' && <LocationPopupTrend dataProgress={dataProgress} />}

      {hasData && (
        <div className="mb-3">
          <LocationPopupBreakdown
            locationId={location.id}
            dataLayer={dataLayer}
            currentWeek={currentWeek}
            hasData={hasData}
          />
        </div>
      )}

      {hasData && (
        <div className="mt-3 pt-2 border-t grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-gray-600">{t('map.popup.site', 'Site')}:</span>
            <span className="ml-1 font-medium">{location.siteId}</span>
          </div>
          <div>
            <span className="text-gray-600">{t('region.name.label', 'Region')}:</span>
            <span className="ml-1 font-medium">{location.regionId}</span>
          </div>
        </div>
      )}
    </div>
  );
}
